/**
 * Модальное окно макета. Порядок блоков внутри `.modal` жёсткий:
 * head → actions → (крошки стадий) → tabs → body.
 *
 * Что делает компонент сверх макета:
 *  - блокирует прокрутку `body` с компенсацией ширины полосы прокрутки (в макете этого нет,
 *    из-за чего колесо над оверлеем крутило страницу под ним);
 *  - считает вложенные модалки: `inert` на `.app-container` и блокировка скролла снимаются
 *    только когда закрылась последняя. Счётчик общий на приложение и вынесен в
 *    `useModalLayer()` — карточка дела рисует свой каркас, но встаёт в тот же стек;
 *  - ловушка фокуса и Escape работают только у верхнего окна стека.
 *
 * `.modal-actions:empty` и `.modal-tabs:empty` прячут себя сами, поэтому контейнеры
 * рендерятся всегда — пустые они схлопнутся.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

const FOCUSABLE =
    'button:not([disabled]), select, textarea, input, a[href], [tabindex]:not([tabindex="-1"])';

/* Стек открытых окон: верхнее забирает Escape и Tab. */
const stack: string[] = [];

let scrollLocks = 0;
let savedOverflow = '';
let savedPaddingRight = '';

function lockScroll(): void {
    if (scrollLocks === 0) {
        const gap = window.innerWidth - document.documentElement.clientWidth;
        savedOverflow = document.body.style.overflow;
        savedPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = 'hidden';
        if (gap > 0) document.body.style.paddingRight = gap + 'px';
    }
    scrollLocks += 1;
}

function unlockScroll(): void {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) {
        document.body.style.overflow = savedOverflow;
        document.body.style.paddingRight = savedPaddingRight;
    }
}

function setInert(on: boolean): void {
    const app = document.querySelector('.app-container');
    if (!app) return;
    if (on) app.setAttribute('inert', '');
    else app.removeAttribute('inert');
}

/**
 * Открыто ли сейчас другое окно. Нужно карточке дела: если её открыли из модального
 * списка (KPI-плитка, календарь), она рисуется поверх него — `nested` поднимает z-index,
 * а закрытие возвращает пользователя к списку, как «← Назад к списку» в макете.
 */
export function hasOpenModal(): boolean {
    return stack.length > 0;
}

let layerSeq = 0;

/**
 * Общий слой модального окна: единственный владелец `inert` на `.app-container`
 * и блокировки прокрутки во всём приложении. Любое окно — и `Modal`, и карточка
 * дела с собственным каркасом — обязано вставать в этот стек, иначе закрытие
 * верхнего окна снимет `inert` при открытом нижнем и фон снова станет доступен
 * с клавиатуры (ui-case-card.md §3.6).
 *
 * Возвращает `true`, если на момент открытия под окном уже было другое: такому
 * окну нужен класс `nested` — иначе порядок держался бы только на порядке
 * добавления порталов при равном `z-index`.
 */
export function useModalLayer(): boolean {
    const [nested] = useState(() => stack.length > 0);
    useEffect(() => {
        layerSeq += 1;
        const id = 'layer-' + layerSeq;
        stack.push(id);
        lockScroll();
        setInert(true);
        return () => {
            const index = stack.indexOf(id);
            if (index >= 0) stack.splice(index, 1);
            unlockScroll();
            if (stack.length === 0) setInert(false);
        };
    }, []);
    return nested;
}

export interface ModalBackButton {
    label?: string;
    onClick: () => void;
}

export interface ModalProps {
    /** Заголовок `<h3>`; он же `aria-labelledby` для `role="dialog"`. */
    title: ReactNode;
    /** Строка `.m-id` над заголовком: «ГП3-2026-000142 · приоритет 2». */
    idLine?: ReactNode;
    /** Чипы `.m-chips span` под заголовком. */
    chips?: ReactNode;
    /** Кнопка «← Назад к списку»: показывается, когда карточка открыта из списка. */
    back?: ModalBackButton | null;
    /** Панель действий `.modal-actions` — она относится ко всему делу, а не к вкладке. */
    actions?: ReactNode;
    /** Полоса между действиями и вкладками: крошки стадий `<StageTrack/>`. */
    belowActions?: ReactNode;
    /** Кнопки вкладок `.modal-tabs button`. */
    tabs?: ReactNode;
    children?: ReactNode;
    onClose: () => void;
    /** Узкое окно для подтверждений поверх карточки. */
    size?: 'default' | 'small';
    /** Окно поверх другого окна: выше по z-index, не трогает `inert` повторно. */
    nested?: boolean;
    /** При смене значения `.modal-body` прокручивается наверх (макет делает это при смене вкладки). */
    resetScrollKey?: string | number;
}

export function Modal({
    title,
    idLine,
    chips,
    back,
    actions,
    belowActions,
    tabs,
    children,
    onClose,
    size = 'default',
    nested = false,
    resetScrollKey,
}: ModalProps) {
    const titleId = useId();
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const closeRef = useRef<HTMLButtonElement | null>(null);
    const restoreFocusRef = useRef<HTMLElement | null>(null);

    // Счётчик окон, `inert` и блокировка прокрутки — общие для всех модалок.
    const autoNested = useModalLayer();

    // Монтирование: запомнить фокус, увести его на «закрыть», вернуть при закрытии.
    useEffect(() => {
        restoreFocusRef.current = document.activeElement as HTMLElement | null;
        closeRef.current?.focus();
        return () => {
            const target = restoreFocusRef.current;
            if (target && typeof target.focus === 'function') {
                try {
                    target.focus({ preventScroll: true });
                } catch {
                    target.focus();
                }
            }
        };
    }, []);

    const isTop = useCallback(() => {
        const overlay = overlayRef.current;
        if (!overlay) return false;
        const overlays = document.querySelectorAll('.modal-overlay.show');
        return overlays.length === 0 || overlays[overlays.length - 1] === overlay;
    }, []);

    // Escape закрывает, Tab не выпускает фокус наружу — только у верхнего окна.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (!isTop()) return;
            if (event.key === 'Escape') {
                event.stopPropagation();
                onClose();
                return;
            }
            if (event.key !== 'Tab') return;
            const overlay = overlayRef.current;
            if (!overlay) return;
            const nodes = Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
                (node) => node.offsetParent !== null || node === document.activeElement,
            );
            if (nodes.length === 0) return;
            const first = nodes[0];
            const last = nodes[nodes.length - 1];
            const active = document.activeElement as HTMLElement | null;
            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            } else if (!active || nodes.indexOf(active) === -1) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isTop, onClose]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [resetScrollKey]);

    const overlayClass = nested || autoNested ? 'modal-overlay nested show' : 'modal-overlay show';

    return createPortal(
        <div
            className={overlayClass}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            ref={overlayRef}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
            }}
        >
            <div className={size === 'small' ? 'modal modal-sm' : 'modal'}>
                <div className="modal-head">
                    <div className="mh-text">
                        {back && (
                            <button type="button" className="back-btn" onClick={back.onClick}>
                                {back.label ?? '← Назад к списку'}
                            </button>
                        )}
                        {idLine !== undefined && idLine !== null && <div className="m-id">{idLine}</div>}
                        <h3 id={titleId}>{title}</h3>
                        {chips && <div className="m-chips">{chips}</div>}
                    </div>
                    <button type="button" ref={closeRef} onClick={onClose} aria-label="Закрыть">
                        ✕
                    </button>
                </div>

                <div className="modal-actions">{actions}</div>
                {belowActions}
                <div className="modal-tabs" role="tablist">
                    {tabs}
                </div>

                <div className="modal-body" ref={bodyRef}>
                    {children}
                </div>
            </div>
        </div>,
        document.body,
    );
}

/* ─────────────────────────── Навигация к карточке дела ─────────────────────────── */

interface BackgroundState {
    backgroundLocation?: { pathname: string; search: string };
}

/**
 * Открыть карточку дела поверх текущего экрана: адрес меняется на `/cases/:id`,
 * а под модалкой остаётся то, что было (светофор, входящие, список).
 * Прямая ссылка снаружи тоже работает — там под модалкой рисуется список дел.
 */
export function useOpenCase(): (caseId: string) => void {
    const navigate = useNavigate();
    const location = useLocation();
    return useCallback(
        (caseId: string) => {
            navigate('/cases/' + caseId, {
                state: {
                    backgroundLocation: { pathname: location.pathname, search: location.search },
                } satisfies BackgroundState,
            });
        },
        [navigate, location.pathname, location.search],
    );
}

/** Закрыть карточку: вернуться на экран под ней либо в список дел. */
export function useCloseCase(): () => void {
    const navigate = useNavigate();
    const location = useLocation();
    return useCallback(() => {
        const state = location.state as BackgroundState | null;
        if (state?.backgroundLocation) navigate(-1);
        else navigate('/cases', { replace: true });
    }, [navigate, location.state]);
}

export default Modal;
